/**
 * Admin Auth Store (SCRUM-153)
 *
 * Zustand store for admin authentication state management.
 * Uses centralized token utilities from @/utils/token
 *
 * Features:
 * - login: Set tokens and update user state
 * - logout: Clear tokens and reset user state
 * - initialize: Restore user from existing token
 */

import { create } from 'zustand';
import {
  getAccessToken,
  getUserInfo,
  parseJwt,
  setTokens,
  removeTokens
} from '@/utils/token';

interface AdminUser {
  username: string;
  name: string;
  role: string;
  roles: string[];
  email?: string;
  organizationId?: number;
  organizationName?: string;
}

interface OrganizationInfo {
  id: number;
  name: string;
  code?: string;
  businessTypeCode?: string;
}

interface AdminAuthStore {
  user: AdminUser | null;
  isAuthenticated: boolean;
  initialize: () => void;
  login: (
    accessToken: string,
    refreshToken: string,
    username: string,
    name: string,
    email: string,
    roles: string[],
    organization?: OrganizationInfo
  ) => void;
  logout: () => void;
}

function getUserFromToken(): AdminUser | null {
  const token = getAccessToken();
  if (!token) return null;

  const payload = parseJwt(token);
  if (!payload) return null;

  const roles = (payload.roles as string[]) || [];
  const role = roles.includes('SUPER_ADMIN') ? 'SUPER_ADMIN' :
               roles.includes('ADMIN') ? 'ADMIN' :
               roles.includes('ORG_ADMIN') ? 'ORG_ADMIN' : 'UNKNOWN';

  // JWT에 organizationId가 없으므로 localStorage에서 복원
  const { organization } = getUserInfo();

  return {
    username: (payload.sub as string) || '',
    name: (payload.name as string) || (payload.sub as string) || '관리자',
    role,
    roles,
    email: payload.email as string | undefined,
    organizationId: organization?.id ?? undefined,
    organizationName: organization?.name ?? undefined,
  };
}

export const useAdminAuthStore = create<AdminAuthStore>((set) => ({
  user: getUserFromToken(),
  isAuthenticated: !!getAccessToken(),

  initialize: () => {
    const user = getUserFromToken();
    set({ user, isAuthenticated: !!user });
  },

  login: (accessToken, refreshToken, username, name, email, roles, organization) => {
    // Store tokens in localStorage
    setTokens(accessToken, refreshToken, username, name, email, roles, organization);

    // Update Zustand state
    const role = roles.includes('SUPER_ADMIN') ? 'SUPER_ADMIN' :
                 roles.includes('ADMIN') ? 'ADMIN' :
                 roles.includes('ORG_ADMIN') ? 'ORG_ADMIN' : 'UNKNOWN';

    set({
      user: {
        username, name, role, roles, email,
        organizationId: organization?.id ?? undefined,
        organizationName: organization?.name ?? undefined,
      },
      isAuthenticated: true,
    });
  },

  logout: () => {
    // Clear tokens from localStorage
    removeTokens();

    // Reset Zustand state
    set({ user: null, isAuthenticated: false });
  },
}));
